import React, {Fragment, useCallback, useEffect, useState} from 'react';
import {useDispatch} from 'react-redux';
import {Form, Field} from 'react-final-form';
import {
    RESET_PASSWORD_SUCCESS,
    RESET_PASSWORD_CODE_SUCCESS,
    RESET_PASSWORD_NEW_PASSWORD_SUCCESS,
    resetPassword,
    resetPasswordCode,
    resetPasswordNewPassword,
} from '@core/actions/UserActions';
import useInvisibleRecaptcha from '@core/hooks/useInvisibleRecaptcha';
import {
    CAPTCHA_CONFIRM_MESSAGE,
    CAPTCHA_ERRORS,
    CAPTCHA_FAILED_MESSAGE,
    CAPTCHA_MAX_ATTEMPTS,
} from '@core/constants/CaptchaConstants';
import {INVALID_EMAIL} from '@common/constants/ApiErrorCode';
import {composeValidators, required, email, limit, minLength} from '@form/helpers/ValidateHelper';

const STEPS = ['email', 'code', 'new-password', 'success'];
const QUERY_EMAIL = 'resetEmail';
const QUERY_CODE = 'resetCode';
const QUERY_HAS_OWN_PASSWORD = 'resetOwnPassword';

// URL-synced step: refresh keeps step, Back/Forward navigates between steps
function useUrlStep() {
    const read = () => {
        try {
            const s = new URLSearchParams(window.location.search).get('step');
            return STEPS.includes(s) ? s : 'email';
        } catch {
            return 'email';
        }
    };
    const [step, setStepState] = useState(read);
    const setStep = useCallback((next) => {
        setStepState(next);
        const url = new URL(window.location.href);
        next === 'email' ? url.searchParams.delete('step') : url.searchParams.set('step', next);
        window.history.pushState(null, '', url.toString());
    }, []);
    useEffect(() => {
        const on = () => setStepState(read());
        window.addEventListener('popstate', on);
        return () => window.removeEventListener('popstate', on);
    }, []);
    return [step, setStep];
}

// Query-synced context: survives refresh without relying on browser storage.
function useQueryContext() {
    const read = () => {
        try {
            const params = new URLSearchParams(window.location.search);
            const hasOwnPassword = params.get(QUERY_HAS_OWN_PASSWORD);

            return {
                email: params.get(QUERY_EMAIL) || '',
                code: params.get(QUERY_CODE) || '',
                hasOwnPassword: hasOwnPassword === null ? undefined : hasOwnPassword === '1',
            };
        } catch {
            return {};
        }
    };
    const [ctx, setCtx] = useState(read);

    const write = (next) => {
        try {
            const url = new URL(window.location.href);
            const params = url.searchParams;

            next.email ? params.set(QUERY_EMAIL, next.email) : params.delete(QUERY_EMAIL);
            next.code ? params.set(QUERY_CODE, next.code) : params.delete(QUERY_CODE);

            if (typeof next.hasOwnPassword === 'boolean') {
                params.set(QUERY_HAS_OWN_PASSWORD, next.hasOwnPassword ? '1' : '0');
            } else {
                params.delete(QUERY_HAS_OWN_PASSWORD);
            }

            window.history.replaceState(window.history.state, '', url.toString());
        } catch {
        }
    };

    const patch = useCallback((p) => {
        setCtx(prev => {
            const next = {...prev, ...p};
            write(next);
            return next;
        });
    }, []);
    const clear = useCallback(() => {
        setCtx({});
        try {
            const url = new URL(window.location.href);
            url.searchParams.delete(QUERY_EMAIL);
            url.searchParams.delete(QUERY_CODE);
            url.searchParams.delete(QUERY_HAS_OWN_PASSWORD);
            url.searchParams.delete('step');
            window.history.replaceState(window.history.state, '', url.toString());
        } catch {
        }
    }, []);

    useEffect(() => {
        const on = () => setCtx(read());
        window.addEventListener('popstate', on);
        return () => window.removeEventListener('popstate', on);
    }, []);

    return [ctx, patch, clear];
}

// Is a step allowed given the context? (email + code required for later steps)
const stepRequirements = {
    'email': () => true,
    'code': ctx => !!ctx.email,
    'new-password': ctx => !!ctx.email && !!ctx.code,
    'success': ctx => !!ctx.email,
};

function ResumeInputField({
                              input,
                              className,
                              placeholder,
                              autoComplete,
                              inputProps,
                              disabled,
                              meta: {touched, error, dirtySinceLastSubmit, submitError}
                          }) {
    const hasError = (touched && error) || (!dirtySinceLastSubmit && submitError);
    return <input
        {...input}
        {...inputProps}
        className={className + (hasError ? ' resume-auth__input--error' : '')}
        placeholder={placeholder}
        autoComplete={autoComplete}
        disabled={disabled}
    />;
}

function ResumeError({name}) {
    return <Field
        name={name}
        subscribe={{touched: true, error: true, submitError: true, dirtySinceLastSubmit: true}}
        render={({meta: {touched, error, submitError, dirtySinceLastSubmit}}) => {
            const visibleError = ((touched && error) || (!dirtySinceLastSubmit && submitError))
                ? (error || submitError)
                : '';
            return <div className="resume-auth__error">{visibleError}</div>;
        }}
    />;
}

function ResumeResetPassword({initialEmail, onSignIn}) {
    const dispatch = useDispatch();
    const {execute: executeRecaptcha, enabled: recaptchaEnabled} = useInvisibleRecaptcha();
    const [step, setStep] = useUrlStep();
    const [ctx, patchCtx, clearCtx] = useQueryContext();

    // Seed email from prop on first mount if ctx is empty
    useEffect(() => {
        if (initialEmail && !ctx.email) patchCtx({email: initialEmail});
    }, []);

    // Guard: if URL asks for a step we don't have context for, downgrade synchronously
    // so we never render a Form that will immediately unmount.
    const effectiveStep = stepRequirements[step](ctx) ? step : 'email';
    useEffect(() => {
        if (effectiveStep !== step) setStep(effectiveStep);
    }, [effectiveStep, step, setStep]);

    const hasOwnPassword = ctx.hasOwnPassword !== false;

    const getErrorCode = action => {
        const payload = action?.payload ?? {};
        const body = payload.response ?? {};
        if (typeof body.error === 'string') return body.error;
        if (typeof body.message === 'string') return body.message;
        if (typeof body.description === 'string') return body.description;
        if (typeof body?.errors?.detail === 'string') return body.errors.detail;
        const emails = (body.details ?? payload.details)?.email;
        if (Array.isArray(emails) && emails.length) return INVALID_EMAIL;
        return null;
    };

    const sendResetRequest = useCallback(async (values, token = '', attempt = 0) => {
        if (!token && recaptchaEnabled && attempt === 0) {
            const got = await executeRecaptcha();
            if (!got) return undefined;
            token = got;
            attempt = 1;
        }

        const response = await dispatch(resetPassword({...values, recaptchaResponse: token}));
        const errorCode = getErrorCode(response);

        if (errorCode === INVALID_EMAIL) return {email: 'This user does not exist. Try again.'};
        if (CAPTCHA_ERRORS.includes(errorCode)) {
            if (!recaptchaEnabled || attempt >= CAPTCHA_MAX_ATTEMPTS) return {email: CAPTCHA_CONFIRM_MESSAGE};
            const retry = await executeRecaptcha();
            return retry ? sendResetRequest(values, retry, attempt + 1) : {email: CAPTCHA_FAILED_MESSAGE};
        }
        if (errorCode || response.type !== RESET_PASSWORD_SUCCESS || response.payload?.success !== true) {
            return {email: 'Try again later.'};
        }

        patchCtx({
            email: values.email,
            hasOwnPassword: response.payload.response?.has_own_password !== false,
        });
        setStep('code');
    }, [dispatch, executeRecaptcha, recaptchaEnabled, patchCtx, setStep]);

    const onSubmitEmail = useCallback((values) => sendResetRequest(values), [sendResetRequest]);

    const onSubmitCode = useCallback(async (values) => {
        const response = await dispatch(resetPasswordCode({email: ctx.email, code: values.code}));
        if (getErrorCode(response)) return {code: 'Invalid code. Please try again.'};
        if (response.type !== RESET_PASSWORD_CODE_SUCCESS || response.payload?.success !== true) {
            return {code: 'Try again later.'};
        }
        patchCtx({code: values.code});
        setStep('new-password');
    }, [dispatch, ctx.email, patchCtx, setStep]);

    const onSubmitNewPassword = useCallback(async (values) => {
        const response = await dispatch(resetPasswordNewPassword({
            email: ctx.email,
            code: ctx.code,
            newPassword: values.newPassword,
            confirmPassword: values.confirmPassword,
        }));
        if (getErrorCode(response)) return {newPassword: 'Failed to set password. Try again later.'};
        if (response.type !== RESET_PASSWORD_NEW_PASSWORD_SUCCESS || response.payload?.success !== true) {
            return {newPassword: 'Try again later.'};
        }
        patchCtx({code: ''});
        setStep('success');
    }, [dispatch, ctx.email, ctx.code, patchCtx, setStep]);

    const goSignIn = () => {
        clearCtx();
        onSignIn();
    };

    if (effectiveStep === 'email') return (
        <div className="resume-auth__reset-password">
            <h2 className="resume-auth__title">Reset Password</h2>
            <Form key="email" initialValues={{email: ctx.email || ''}} onSubmit={onSubmitEmail}>
                {({handleSubmit, submitting}) => (
                    <form onSubmit={handleSubmit} className="resume-auth__form" noValidate>
                        <div className="resume-auth__form-group">
                            <label className="resume-auth__label">Email <span className="resume-auth__required">*</span></label>
                            <Field
                                name="email"
                                type="email"
                                component={ResumeInputField}
                                placeholder="e.g. Email@address.com"
                                className="resume-auth__input"
                                autoComplete="email"
                                inputProps={{
                                    inputMode: 'email',
                                    autoCorrect: 'off',
                                    spellCheck: 'false',
                                    autoCapitalize: 'none'
                                }}
                                validate={composeValidators(
                                    required('Please provide an email'),
                                    email('Please provide a valid email'),
                                    limit('Maximum {limit} characters allowed. You\'ve used {count}.', 320)
                                )}
                            />
                            <ResumeError name="email"/>
                        </div>
                        <div className="resume-auth__form-actions">
                            <button type="submit" className="resume-auth__btn" disabled={submitting}>
                                {submitting ? 'Sending...' : 'Send Request'}
                            </button>
                        </div>
                        <p className="resume-auth__switch-text">
                            Remember your password?
                            <a onClick={goSignIn} className="resume-auth__switch-link" role="button" tabIndex={0}>Sign
                                In</a>
                        </p>
                    </form>
                )}
            </Form>
        </div>
    );

    if (effectiveStep === 'code') return (
        <div className="resume-auth__reset-password">
            <h2 className="resume-auth__title">Enter Verification Code</h2>
            <Form key="code" onSubmit={onSubmitCode}>
                {({handleSubmit, submitting}) => (
                    <form onSubmit={handleSubmit} className="resume-auth__form" noValidate>
                        <p className="resume-auth__description">
                            We sent a 6-digit code to <strong>{ctx.email}</strong> Please enter it below.
                        </p>
                        <div className="resume-auth__form-group">
                            <label className="resume-auth__label">Code <span className="resume-auth__required">*</span></label>
                            <Field
                                name="code"
                                component={ResumeInputField}
                                placeholder="Enter 6-digit code"
                                className="resume-auth__input"
                                inputProps={{inputMode: 'numeric', maxLength: 6, autoComplete: 'one-time-code'}}
                                validate={composeValidators(
                                    required('Please enter the code'),
                                    v => v && !/^\d{6}$/.test(v) ? 'Code must be 6 digits' : undefined
                                )}
                            />
                            <ResumeError name="code"/>
                        </div>
                        <div className="resume-auth__form-actions">
                            <button type="submit" className="resume-auth__btn" disabled={submitting}>
                                {submitting ? 'Verifying...' : 'Verify Code'}
                            </button>
                        </div>
                        <p className="resume-auth__switch-text">
                            <a href="#" onClick={e => {
                                e.preventDefault();
                                setStep('email');
                            }} className="resume-auth__switch-link">Back</a>
                        </p>
                    </form>
                )}
            </Form>
        </div>
    );

    if (effectiveStep === 'new-password') return (
        <div className="resume-auth__reset-password">
            <h2 className="resume-auth__title">{hasOwnPassword ? 'Set New Password' : 'Set Up Your Password'}</h2>
            <Form key="new-password" onSubmit={onSubmitNewPassword}>
                {({handleSubmit, submitting, values}) => (
                    <form onSubmit={handleSubmit} className="resume-auth__form" noValidate>
                        <div className="resume-auth__form-group">
                            <label className="resume-auth__label">New Password <span
                                className="resume-auth__required">*</span></label>
                            <Field
                                name="newPassword"
                                type="password"
                                component={ResumeInputField}
                                placeholder="Enter new password"
                                className="resume-auth__input"
                                autoComplete="new-password"
                                validate={composeValidators(
                                    required('Please enter a new password'),
                                    minLength('Password must be at least {min} characters', 5),
                                    limit('Maximum {limit} characters allowed. You\'ve used {count}.', 255)
                                )}
                            />
                            <ResumeError name="newPassword"/>
                        </div>
                        <div className="resume-auth__form-group">
                            <label className="resume-auth__label">Confirm Password <span
                                className="resume-auth__required">*</span></label>
                            <Field
                                name="confirmPassword"
                                type="password"
                                component={ResumeInputField}
                                placeholder="Confirm new password"
                                className="resume-auth__input"
                                autoComplete="new-password"
                                validate={composeValidators(
                                    required('Please confirm your password'),
                                    v => v && values.newPassword && v !== values.newPassword ? 'Passwords do not match' : undefined
                                )}
                            />
                            <ResumeError name="confirmPassword"/>
                        </div>
                        <div className="resume-auth__form-actions">
                            <button type="submit" className="resume-auth__btn" disabled={submitting}>
                                {submitting ? 'Saving...' : 'Set New Password'}
                            </button>
                        </div>
                    </form>
                )}
            </Form>
        </div>
    );

    return (
        <div className="resume-auth__reset-password">
            <h2 className="resume-auth__title">{hasOwnPassword ? 'Password Changed' : 'Password Set Up'}</h2>
            <div className="resume-auth__success">
                <p>{hasOwnPassword
                    ? 'Your password has been successfully changed.'
                    : 'Your password has been successfully set up. You can now sign in with your email and password.'}</p>
            </div>
            <div className="resume-auth__form-actions">
                <button type="button" className="resume-auth__btn" onClick={goSignIn}>Sign In</button>
            </div>
        </div>
    );
}

export default ResumeResetPassword;
